import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ThumbsUp, ThumbsDown, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";

function Stars({ value, onPick, size = "h-5 w-5", testid }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map((i) => (
        <button key={i} type="button" disabled={!onPick} onClick={() => onPick?.(i)} onMouseEnter={() => onPick && setHover(i)}
          data-testid={testid ? `${testid}-${i}` : undefined} className={onPick ? "transition-transform hover:scale-110" : "cursor-default"}>
          <Star className={`${size} ${(hover || value) >= i ? "text-amber-400 fill-current" : "text-muted-foreground/40"}`} />
        </button>
      ))}
    </div>
  );
}

function ReviewForm({ slug, mine, onDone }) {
  const [rating, setRating] = useState(mine?.rating || 0);
  const [liked, setLiked] = useState(mine?.liked ?? null);
  const [text, setText] = useState(mine?.review || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!rating) return toast.error("Please pick a star rating");
    setBusy(true);
    try { await api.put(`/books/${slug}/review`, { rating, liked, review: text }); toast.success(mine ? "Review updated" : "Thanks for your review!"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  const likeBtn = (val, Icon, label) => (
    <button type="button" onClick={() => setLiked(liked === val ? null : val)} data-testid={`review-liked-${val ? "yes" : "no"}`}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition-colors ${liked === val ? "border-primary bg-primary/15 text-primary" : "border-border hover:border-primary"}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
  return (
    <div className="rounded-2xl border border-border surface-2 p-6 space-y-4" data-testid="review-form">
      <div><p className="text-sm font-semibold mb-2">Your rating</p><Stars value={rating} onPick={setRating} size="h-7 w-7" testid="review-star" /></div>
      <div><p className="text-sm font-semibold mb-2">Did you like this book?</p><div className="flex gap-2">{likeBtn(true, ThumbsUp, "Loved it")}{likeBtn(false, ThumbsDown, "Not for me")}</div></div>
      <textarea rows={4} maxLength={2000} value={text} onChange={(e) => setText(e.target.value)} placeholder="Share your thoughts (optional)…" data-testid="review-text"
        className="w-full bg-background border border-border rounded-xl px-4 py-3 text-sm outline-none focus:border-primary" />
      <div className="flex items-center gap-3">
        <button onClick={submit} disabled={busy} data-testid="review-submit" className="px-6 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold text-sm disabled:opacity-50">{busy ? "Saving…" : mine ? "Update review" : "Submit review"}</button>
        {mine && <button onClick={onDone} className="text-sm text-muted-foreground" data-testid="review-cancel">Cancel</button>}
      </div>
    </div>
  );
}

function Summary({ d }) {
  return (
    <div className="rounded-2xl border border-border surface-2 p-6" data-testid="review-summary">
      <p className="text-5xl font-display font-bold" data-testid="review-average">{d.count ? Number(d.average).toFixed(1) : "–"}</p>
      <Stars value={Math.round(d.average)} />
      <p className="text-sm text-muted-foreground mt-1" data-testid="review-count">{d.count} rating{d.count === 1 ? "" : "s"}{d.count ? ` · ${d.liked_pct}% liked it` : ""}</p>
      <div className="space-y-1.5 mt-4">
        {[5, 4, 3, 2, 1].map((s) => (
          <div key={s} className="flex items-center gap-2 text-xs">
            <span className="w-3">{s}</span><Star className="h-3 w-3 text-amber-400 fill-current" />
            <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-amber-400" style={{ width: `${d.count ? (d.distribution[s] * 100) / d.count : 0}%` }} /></div>
            <span className="w-6 text-right text-muted-foreground">{d.distribution[s]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewItem({ r, mine, onEdit, onDelete }) {
  return (
    <div className="py-5 border-b border-border/60" data-testid={mine ? "my-review" : `review-${r.id}`}>
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-primary/15 text-primary grid place-items-center font-semibold overflow-hidden">{r.user_avatar ? <img src={r.user_avatar} alt="" className="h-full w-full object-cover" /> : (r.user_name || "R")[0].toUpperCase()}</div>
        <div className="flex-1"><p className="text-sm font-semibold">{r.user_name}{mine && <span className="text-xs text-primary ml-2">(You)</span>}</p>
          <div className="flex items-center gap-2"><Stars value={r.rating} size="h-3.5 w-3.5" /><span className="text-xs text-muted-foreground">{new Date(r.updated_at).toLocaleDateString()}</span>
            {r.liked === true && <span className="text-xs text-primary inline-flex items-center gap-1"><ThumbsUp className="h-3 w-3" />Loved it</span>}
            {r.liked === false && <span className="text-xs text-muted-foreground inline-flex items-center gap-1"><ThumbsDown className="h-3 w-3" />Not for me</span>}</div></div>
        {mine && <div className="flex gap-2">
          <button onClick={onEdit} data-testid="my-review-edit" className="p-2 rounded-lg border border-border hover:border-primary"><Pencil className="h-4 w-4" /></button>
          <button onClick={onDelete} data-testid="my-review-delete" className="p-2 rounded-lg border border-border hover:border-destructive text-destructive"><Trash2 className="h-4 w-4" /></button>
        </div>}
      </div>
      {r.review && <p className="text-sm text-foreground/85 mt-3 whitespace-pre-line leading-relaxed">{r.review}</p>}
    </div>
  );
}

export default function Reviews({ slug, onChange }) {
  const { user } = useApp();
  const [d, setD] = useState(null);
  const [editing, setEditing] = useState(false);
  const [page, setPage] = useState(1);
  const load = (p = 1) => api.get(`/books/${slug}/reviews?page=${p}`).then((r) => {
    setPage(p); setD((prev) => (p > 1 && prev ? { ...r.data, reviews: [...prev.reviews, ...r.data.reviews] } : r.data));
  });
  useEffect(() => { load(1); setEditing(false); }, [slug, user]); // eslint-disable-line
  if (!d) return null;
  const done = () => { setEditing(false); load(1); onChange?.(); };
  const del = async () => {
    if (!window.confirm("Delete your review?")) return;
    try { await api.delete(`/books/${slug}/review`); toast.success("Review deleted"); done(); } catch (e) { toast.error(apiError(e)); }
  };
  const others = d.reviews.filter((r) => r.id !== d.my_review?.id);
  return (
    <section id="reviews" className="scroll-mt-24" data-testid="reviews-section">
      <h2 className="text-2xl font-display font-bold mb-6">Ratings & Reviews</h2>
      <div className="grid md:grid-cols-[280px_1fr] gap-8">
        <Summary d={d} />
        <div>
          {!user && <div className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="review-login-prompt"><Link to="/login" className="text-primary font-semibold">Sign in</Link> to rate this book and share your review.</div>}
          {user && (!d.my_review || editing) && <ReviewForm key={d.my_review?.updated_at || "new"} slug={slug} mine={d.my_review} onDone={done} />}
          {user && d.my_review && !editing && <ReviewItem r={d.my_review} mine onEdit={() => setEditing(true)} onDelete={del} />}
          {others.map((r) => <ReviewItem key={r.id} r={r} />)}
          {!d.count && <p className="text-sm text-muted-foreground mt-6" data-testid="no-reviews">No reviews yet — be the first to rate this book.</p>}
          {d.has_more && <button onClick={() => load(page + 1)} data-testid="reviews-load-more" className="mt-4 text-sm text-primary">Show more reviews</button>}
        </div>
      </div>
    </section>
  );
}
